import type { PayloadAction, SliceCaseReducers } from "@reduxjs/toolkit";
import type {
  OutputImage,
  SpecsEditorSession,
  SpecsLayer,
  SpecsLayerRuntimeState,
} from "../../../../containers/SpecsEditor/types";
import { SpecsImageProcessingStatus } from "../../../../containers/SpecsEditor/types";
import {
  createLayerNavFromLayer,
  generateId,
  getPreferredNewLayerScope,
  touch,
} from "../helpers";
import {
  createDefaultOutputImage,
  createDefaultOutputImageSide,
  getOutputImageSide,
  setOutputImageSide,
} from "./images";
import {
  makeLayerRuntimeKey,
  makeOutputImageRuntimeId,
} from "../../../../containers/SpecsEditor/specsEditorRuntimeKeys";

function defineLayerReducers<T extends SliceCaseReducers<SpecsEditorSession>>(
  reducers: T
) {
  return reducers;
}

export const layerReducers = defineLayerReducers({
  addLayer(state, { payload }: PayloadAction<string | undefined>) {
    const name = payload ?? `Layer ${state.layerRuntimeOrder.length + 1}`;
    const frontSourceId =
      state.input?.layers.length === 1 ? state.input.layers[0].id : null;
    const preferredScope = getPreferredNewLayerScope(state);
    const highlight = createDefaultOutputImage(
      `${name} Highlight`,
      "highlight"
    );
    const mask = createDefaultOutputImage(`${name} Mask`, "mask");
    if (frontSourceId) {
      const highlightFront = getOutputImageSide(highlight, "front");
      const maskFront = getOutputImageSide(mask, "front");
      if (highlightFront) {
        highlightFront.sourceLayerId = frontSourceId;
        setOutputImageSide(highlight, "front", highlightFront);
      }
      if (maskFront) {
        maskFront.sourceLayerId = frontSourceId;
        setOutputImageSide(mask, "front", maskFront);
      }
    }
    const modelLayerPath = {
      partName: preferredScope?.partName ?? "",
      elementName: preferredScope?.elementName ?? "",
      styleName: preferredScope?.styleName ?? "",
      layerName: name,
    };
    const layerKey = makeLayerRuntimeKey(modelLayerPath);
    highlight.id = makeOutputImageRuntimeId({
      layerKey,
      role: "highlight",
    });
    mask.id = makeOutputImageRuntimeId({ layerKey, role: "mask" });
    state.layerRuntimeByKey[layerKey] = {
      key: layerKey,
      id: layerKey,
      modelLayerPath,
      visible: true,
      baseColor: state.previewColor,
      zIndex: (state.layerRuntimeOrder.length + 1) * 100,
      sideIndexes: {},
      highlight,
      mask,
    };
    state.layerRuntimeOrder.push(layerKey);
    state.nav = createLayerNavFromLayer(
      state,
      state.layerRuntimeByKey[layerKey]
    );
    touch(state);
  },

  removeLayer(state, { payload: layerId }: PayloadAction<string>) {
    delete state.layerRuntimeByKey[layerId];
    state.layerRuntimeOrder = state.layerRuntimeOrder.filter(
      (key) => key !== layerId
    );
    touch(state);
  },

  ensureLayerHighlight(state, { payload: layerId }: PayloadAction<string>) {
    const layer = state.layerRuntimeByKey[layerId];
    if (!layer || layer.highlight) return;

    const highlight = createDefaultOutputImage(
      `${layer.modelLayerPath.layerName || "Layer"} Highlight`,
      "highlight"
    );
    const highlightFront = getOutputImageSide(highlight, "front");
    if (state.input?.layers.length === 1 && highlightFront) {
      highlightFront.sourceLayerId = state.input.layers[0].id;
      setOutputImageSide(highlight, "front", highlightFront);
    }
    if (layer.mask && getOutputImageSide(layer.mask, "back")) {
      const highlightBack = createDefaultOutputImageSide("highlight");
      if (state.backInput?.layers.length === 1) {
        highlightBack.sourceLayerId = state.backInput.layers[0].id;
      }
      setOutputImageSide(highlight, "back", highlightBack);
    }

    layer.highlight = highlight;
    touch(state);
  },

  toggleLayerVisibility(state, { payload: layerId }: PayloadAction<string>) {
    const layer = state.layerRuntimeByKey[layerId];
    if (!layer) return;
    layer.visible = !layer.visible;
    touch(state);
  },

  updateLayerColor(
    state,
    {
      payload,
    }: PayloadAction<{ layerId: string; color: string; layer?: SpecsLayer }>
  ) {
    let layer = state.layerRuntimeByKey[payload.layerId];
    if (!layer && payload.layer?.modelLayerPath) {
      layer = {
        ...payload.layer,
        key: payload.layerId,
        id: payload.layerId,
        modelLayerPath: payload.layer.modelLayerPath,
        sideIndexes: {},
        mask: payload.layer.mask!,
      };
      state.layerRuntimeByKey[payload.layerId] = layer;
    }
    if (!layer) return;
    layer.baseColor = payload.color;
    touch(state);
  },

  updateLayerZIndex(
    state,
    { payload }: PayloadAction<{ layerId: string; zIndex: number }>
  ) {
    const layer = state.layerRuntimeByKey[payload.layerId];
    if (!layer) return;
    layer.zIndex = payload.zIndex;
    touch(state);
  },

  updateLayer(
    state,
    {
      payload,
    }: PayloadAction<{
      layerId: string;
      patch: Partial<{ name: string }>;
    }>
  ) {
    const layer = state.layerRuntimeByKey[payload.layerId];
    if (!layer) return;
    if (payload.patch.name !== undefined) {
      layer.modelLayerPath.layerName = payload.patch.name;
    }
    touch(state);
  },

  duplicateLayer(state, { payload: layerId }: PayloadAction<string>) {
    const source = state.layerRuntimeByKey[layerId];
    if (!source) return;
    const cloneImg = (img: OutputImage): OutputImage => {
      const cloned: OutputImage = {
        ...structuredClone(img),
        id: generateId(),
        sides: Object.fromEntries(
          Object.entries(img.sides ?? {}).map(([sideName, side]) => [
            sideName,
            {
              ...structuredClone(side),
              transformations: side.transformations.map((t) => ({
                ...t,
                id: generateId(),
                status: SpecsImageProcessingStatus.IDLE,
                errorMessage: undefined,
              })),
              aiTransform: {
                ...side.aiTransform,
                status: SpecsImageProcessingStatus.IDLE,
                errorMessage: undefined,
              },
            },
          ])
        ),
        back: null,
      };
      const cloneFront = getOutputImageSide(cloned, "front");
      if (cloneFront) {
        cloned.sourceLayerId = cloneFront.sourceLayerId;
        cloned.referenceImageIds = cloneFront.referenceImageIds;
        cloned.transformations = cloneFront.transformations;
        cloned.aiTransform = cloneFront.aiTransform;
        cloned.outputImageUrl = cloneFront.outputImageUrl;
      }
      cloned.back = getOutputImageSide(cloned, "back");
      return cloned;
    };
    const clonePath = {
      ...source.modelLayerPath,
      layerName: `${source.modelLayerPath.layerName ?? source.key} (copy)`,
    };
    const cloneKey = makeLayerRuntimeKey(clonePath);
    const clone: SpecsLayerRuntimeState = {
      key: cloneKey,
      id: cloneKey,
      modelLayerPath: clonePath,
      visible: source.visible,
      baseColor: source.baseColor,
      zIndex: source.zIndex,
      sideIndexes: structuredClone(source.sideIndexes),
      highlight: source.highlight ? cloneImg(source.highlight) : null,
      mask: cloneImg(source.mask),
    };
    state.layerRuntimeByKey[cloneKey] = clone;
    const idx = state.layerRuntimeOrder.findIndex((key) => key === layerId);
    state.layerRuntimeOrder.splice(idx + 1, 0, cloneKey);
    touch(state);
  },

  moveLayer(
    state,
    { payload }: PayloadAction<{ layerId: string; direction: "up" | "down" }>
  ) {
    const idx = state.layerRuntimeOrder.findIndex(
      (key) => key === payload.layerId
    );
    if (idx === -1) return;
    const newIdx = payload.direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= state.layerRuntimeOrder.length) return;
    const [layer] = state.layerRuntimeOrder.splice(idx, 1);
    state.layerRuntimeOrder.splice(newIdx, 0, layer);
    touch(state);
  },

  selectLayer(state, { payload }: PayloadAction<string>) {
    const layer = state.layerRuntimeByKey[payload];
    if (!layer) return;
    state.nav =
      state.nav.kind === "positions"
        ? {
            ...state.nav,
            part: layer.modelLayerPath?.partName ?? "",
            element: layer.modelLayerPath?.elementName ?? "",
            style: layer.modelLayerPath?.styleName ?? "",
            layerId: layer.key,
          }
        : createLayerNavFromLayer(state, layer);
  },

  assignLayerToElement(
    state,
    {
      payload,
    }: PayloadAction<{
      layerId: string;
      elementName: string;
      styleName: string;
      partName?: string;
    }>
  ) {
    const layer = state.layerRuntimeByKey[payload.layerId];
    if (!layer) return;
    const modelLayerPath = layer.modelLayerPath;
    if (!modelLayerPath) {
      layer.modelLayerPath = {
        partName: payload.partName || "body",
        elementName: payload.elementName,
        styleName: payload.styleName,
        layerName: layer.modelLayerPath.layerName ?? layer.key,
      };
    } else {
      layer.modelLayerPath = {
        partName: payload.partName || modelLayerPath.partName,
        elementName: payload.elementName,
        styleName: payload.styleName,
        layerName: modelLayerPath.layerName ?? layer.key,
      };
    }
    touch(state);
  },

  unassignLayer(state, { payload: layerId }: PayloadAction<string>) {
    const layer = state.layerRuntimeByKey[layerId];
    if (!layer) return;
    if (!layer.modelLayerPath) return;
    layer.modelLayerPath = {
      ...layer.modelLayerPath,
      elementName: "",
      styleName: "",
    };
    touch(state);
  },
});
